import ReactApexChart from 'react-apexcharts';
import { withNamespaces } from 'react-i18next';
import {
    Alert,
    Card,
    CardBody,
    CardTitle,
    Col,
    Row,
} from 'reactstrap';

import Knob from 'src/modules/3rd-party-management/components/Knob';

import useSubmissionToTrace from './hooks/useSubmissionToTrace';

const VERIFIED_DDS_KNOB_SETTINGS = {
    height: 100,
    width: 100,
    fgColor: '#59c94f',
    thickness: 0.15,
    lineCap: 'round',
};

const SubmissionToTraceSkeleton = () => (
    <Card className="h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '50%', height: '24px' }} />
            <Row className="g-3 align-items-center">
                <Col xs="12" sm="7">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '220px' }} />
                </Col>
                <Col xs="12" sm="5">
                    <div className="dt-field dt-skeleton mx-auto" style={{ width: '100px', height: '100px' }} />
                </Col>
            </Row>
            <div className="dt-field dt-skeleton mt-4" style={{ width: '100%', height: '48px' }} />
        </CardBody>
    </Card>
);

const SubmissionToTrace = ({
    t,
}) => {
    const {
        segments,
        verifiedPercentage,
        missingDdsCount,
        isLoading,
        isFetching,
        isError,
    } = useSubmissionToTrace({ t });

    if (isLoading || isFetching) {
        return <SubmissionToTraceSkeleton />;
    }

    if (isError || !segments?.length) {
        return null;
    }

    const series = segments.map((segment) => segment.value);
    const labels = segments.map((segment) => segment.label);
    const colors = segments.map((segment) => segment.color);
    const isSeriesEmpty = series.every((value) => value === 0);

    const chartOptions = {
        labels: isSeriesEmpty ? [t('No Data')] : labels,
        colors: isSeriesEmpty ? ['#e0e0e0'] : colors,
        chart: {
            toolbar: {
                show: false,
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '70%',
                },
            },
        },
        dataLabels: {
            enabled: !isSeriesEmpty,
            dropShadow: {
                enabled: false,
            },
            formatter: (val) => `${Math.round(val)}%`,
        },
        legend: {
            show: true,
            position: 'right',
            horizontalAlign: 'center',
            markers: {
                width: 10,
                height: 10,
                radius: 10,
            },
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: (val) => val,
            },
        },
        stroke: {
            width: 0,
        },
        responsive: [
            {
                breakpoint: 768,
                options: {
                    legend: {
                        position: 'bottom',
                    },
                    chart: {
                        height: 260,
                    },
                },
            },
        ],
    };

    return (
        <Card className="h-100 mb-0">
            <CardBody className="d-flex flex-column">
                <CardTitle tag="h6" className="mb-4 overview-section-title ">
                    {t('Submission to TRACE')}
                </CardTitle>

                <Row className="g-3 align-items-center flex-grow-1">
                    <Col xs="12" sm="7">
                        <div className="apex-charts">
                            <ReactApexChart
                                options={chartOptions}
                                series={isSeriesEmpty ? [1] : series}
                                type="donut"
                                height="260"
                            />
                        </div>
                    </Col>

                    <Col xs="12" sm="5">
                        <div className="d-flex flex-column align-items-center justify-content-center text-center">
                            <p className="mb-3 text-muted">
                                {t('Verified DDS by TRACE')}
                            </p>
                            <Knob
                                value={verifiedPercentage}
                                readOnly={true}
                                {...VERIFIED_DDS_KNOB_SETTINGS}
                            />
                        </div>
                    </Col>
                </Row>

                <Alert color="warning" className="mb-0 mt-4">
                    <div className="d-flex justify-content-between align-items-center gap-2">
                        <span>
                            {t('Missing DDS')}
                        </span>
                        <span className="fw-semibold flex-shrink-0">
                            {missingDdsCount}
                        </span>
                    </div>
                </Alert>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(SubmissionToTrace);
